// components/entities/EntityView.tsx
import { isSourceAgentSession } from '#lib/leasing/sourceAgentMode';
import React, { useEffect } from 'react';
import clsx from 'clsx';
import { InventoryEntityType, BaseEntityProps, EntityViewVariant, DealEconomicsTab, LeasingActionType, INVENTORY_ENDPOINTS, EntityRowAction } from '#types/types';
import { BrandRenderer } from './BrandRenderer';
import { CampaignRenderer } from './CampaignRenderer';
import { CAMPAIGN_LEGACY_ENTITY_TYPE } from '#lib/leasing/campaignCard';
import { SourcingCampaignRenderer } from './SourcingCampaignRenderer';
import { PropertyRenderer } from './PropertyRenderer';
import { UnitRenderer } from './UnitRenderer';
import { FloorRenderer } from './FloorRenderer';
import { DealRenderer } from './DealRenderer';
import { MarketSignalRenderer } from './MarketSignalRenderer';
import { DealPage } from './pages/DealPage';
import { BrandShortlistDetail } from '@/components/portal/BrandShortlistDetail';
import { UnitPage } from './pages/UnitPage';
import { SourcingCampaignPage } from './pages/SourcingCampaignPage';
import { FloorPage } from './pages/FloorPage';
import { LeadPage } from './pages/LeadPage';
import { PortfolioPage } from './pages/PortfolioPage';
import { LegalEntityPage } from './pages/LegalEntityPage';
import { OrganisationPage } from './pages/OrganisationPage';
import { MarketSignalPage } from './pages/MarketSignalPage';
import { ProjectPage } from './pages/ProjectPage';
import { AuditPage } from './pages/AuditPage';
import { UserRenderer } from './UserRenderer';
import { TeamRenderer } from './TeamRenderer';
import { ContactRenderer } from './ContactRenderer';
import { OrganisationRenderer } from './OrganisationRenderer';
import { CreditGrantRenderer } from './CreditGrantRenderer';
import { PortfolioRenderer } from './PortfolioRenderer';
import { ProjectRenderer } from './ProjectRenderer';
import { LeadRenderer } from './LeadRenderer';
import type { UnitWorkspaceTab } from './pages/UnitPage';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { TableColumnDef } from '@/components/portal/Table';
import { LegalEntityRenderer } from './LegalEntityRenderer';
import { AuditRenderer } from './AuditRenderer';
import { SlideCorrectionRenderer } from './SlideCorrectionRenderer';

interface EntitySkeletonProps {
  variant?: EntityViewVariant | string;
  className?: string;
}

/**
 * A brand never sits on its own page inside a Source conversation (owner, 2026-09-26: "why do we
 * have this card centered without the rest of the shortlist"). When a Source session's subject is a
 * brand - an older agent turn set it, or a link - the view steps back to the previous subject, the
 * shortlist, and opens that brand's card there. Elsewhere the card is shown as is.
 */
const BrandCardPage = ({ brandId, lang, className }: { brandId: string; lang: 'en' | 'fr'; className?: string }) => {
  const { kernel } = useKernel();
  useEffect(() => {
    const k = kernel.current;
    const sessionId = k?.getActiveChatSessionId();
    const session = sessionId ? k?.getInventory().ChatSession.registry[sessionId] : null;
    if (!k || !sessionId || !isSourceAgentSession(session) || !k.canGoToPreviousSubject(sessionId)) return;
    k.goToPreviousSubject(sessionId);
    const campaignId = k.getState().sourceSelectedCampaignId;
    const lead = (Object.values(k.getInventory().Lead.registry) as Array<{ id?: string; brandId?: string; lastCampaignId?: string | null } | undefined>)
      .find((row) => row?.lastCampaignId === campaignId && row.brandId === brandId);
    if (lead?.id) k.setState({ sourceFocusedFitId: lead.id });
  }, [brandId, kernel]);
  return (
    <div className={clsx('h-full overflow-y-auto p-6', className)}>
      <BrandShortlistDetail brandId={brandId} lang={lang} withHeader />
    </div>
  );
};

export const EntitySkeleton = ({ variant = 'card', className }: EntitySkeletonProps) => {
  switch (variant) {
    case 'chip':
      return <div className={clsx('animate-pulse bg-white/5 rounded-md h-6 w-24 border border-white/10', className)} />;
    case 'row':
      return <div className={clsx('animate-pulse bg-white/5 border-b border-white/5 h-[72px] w-full', className)} />;
    case 'card':
      return <div className={clsx('animate-pulse bg-white/5 rounded-xl border border-white/10 h-[80px] w-full', className)} />;
    case 'page':
      return <div className={clsx('animate-pulse bg-white/5 rounded-xl border border-white/10 h-full min-h-[50vh] w-full', className)} />;
    default:
      return <div className={clsx('animate-pulse bg-white/5 rounded-xl border border-white/10 h-[80px] w-full', className)} />;
  }
};

interface EntityViewProps extends BaseEntityProps {
  type: InventoryEntityType | string;
  dealPageInitialTab?: DealEconomicsTab;
  dealWorkspaceInitialTab?: 'ECONOMICS' | 'CONTACT' | 'DRAFTING' | 'DESIGNS' | 'UNIT';
  focusActionType?: LeasingActionType;
  unitWorkspaceInitialTab?: UnitWorkspaceTab;
  leadLockedUnitId?: string;
  pageHasEntityRowAbove?: boolean;
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<any>[];
  isExpanded?: boolean;
  onDelete?: (entity: any, e: React.MouseEvent) => void;
  /** Credit grant rows only: the invoices their organisation can be assigned to, in place. */
  invoiceOptions?: Array<{ id: string; label: string }>;
}

// Memoized because this is the per-row / per-card dispatcher of every table and grid: callers
// already hand it stable handlers (memoized card wrappers, useCallback row renderers), and each
// instance carries a store subscription plus a sync effect that a parent re-render would replay
// once per visible row.
export const EntityView = React.memo(({
  type,
  entityId,
  variant = 'card',
  dealPageInitialTab,
  dealWorkspaceInitialTab,
  focusActionType,
  unitWorkspaceInitialTab,
  leadLockedUnitId,
  pageHasEntityRowAbove = false,
  columns,
  rowActions,
  ...rest
}: EntityViewProps) => {
  const { kernel } = useKernel();
  // Only meaningful for type === 'Campaign', but a hook cannot be called inside the switch below.
  const campaignTargetKind = useKernelStore((state) => state.inventory.Campaign.registry[entityId]?.targetKind);
  const brandCardLang: 'en' | 'fr' = useKernelStore((state) => (state.language === 'fr' ? 'fr' : 'en'));
  const pageClassName = clsx(pageHasEntityRowAbove && 'ml-4', rest.className);

  useEffect(() => {
    if (variant === 'page' && entityId && type) {
      const isInventoryType = (type: string): type is InventoryEntityType => {
         return Object.prototype.hasOwnProperty.call(INVENTORY_ENDPOINTS, type);
      };
      if (isInventoryType(type)) {
        void kernel.current?.syncEntity(type, entityId);
      }
    }
  }, [entityId, kernel, type, variant]);

  switch (type) {
    case 'Brand': return (
      variant === 'page' ? (
        // ONE brand card. The standalone BrandPage (Overview / Legal entities / Contacts / Leads /
        // Facts) was deleted on the owner's call, 2026-09-26: opening a brand shows the same card as
        // a shortlist row, without the Scoring tab.
        <BrandCardPage brandId={entityId} lang={brandCardLang} className={pageClassName} />
      ) : (
        <BrandRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />
      )
    );
    // A unified campaign carries the SAME id as the legacy row it mirrors (the sync trigger copies
    // NEW."id" verbatim), so `page` hands off to the legacy page for that granularity rather than
    // becoming a fourth copy of it. Only the row/card/chip variants, which a unified list actually
    // needs, render from CampaignDTO.
    case 'Campaign': {
      if (variant !== 'page') {
        return <CampaignRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />;
      }
      // Read through the store, not kernel.current: the row is fetched by the effect above AFTER
      // first render, so a non-reactive read would leave the page on its skeleton with nothing to
      // re-render it once the campaign arrives.
      const legacyType = campaignTargetKind ? CAMPAIGN_LEGACY_ENTITY_TYPE[campaignTargetKind] : undefined;
      if (!legacyType) return <EntitySkeleton variant="page" className={pageClassName} />;
      return (
        <EntityView
          type={legacyType}
          entityId={entityId}
          variant="page"
          pageHasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      );
    }
    case 'SourcingCampaign': return (
      variant === 'page' ? (
        <SourcingCampaignPage
          campaignId={entityId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <SourcingCampaignRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />
      )
    );
    case 'Property': return (
      <PropertyRenderer
        entityId={entityId}
        variant={variant === 'page' ? 'row' : variant}
        columns={columns}
        rowActions={rowActions}
        {...rest}
      />
    );
    case 'Unit': return (
      variant === 'page' ? (
        <UnitPage
          unitId={entityId}
          initialWorkspaceTab={unitWorkspaceInitialTab}
          focusedActionType={focusActionType}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <UnitRenderer
          entityId={entityId}
          variant={variant}
          columns={columns}
          rowActions={rowActions}
          {...rest}
        />
      )
    );
    case 'Floor': return (
      variant === 'page' ? (
        <FloorPage
          floorId={entityId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <FloorRenderer
          entityId={entityId}
          variant={variant}
          columns={columns}
          rowActions={rowActions}
          {...rest}
        />
      )
    );
    case 'Deal': return (
      variant === 'page' ? (
        <DealPage
          dealId={entityId}
          economicsInitialTab={dealPageInitialTab}
          initialWorkspaceTab={dealWorkspaceInitialTab}
          focusedActionType={focusActionType}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <DealRenderer
          entityId={entityId}
          variant={variant}
          columns={columns}
          rowActions={rowActions}
          {...rest}
        />
      )
    );
    case 'MarketSignal': return (
      variant === 'page' ? (
        <MarketSignalPage
          signalId={entityId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <MarketSignalRenderer
          entityId={entityId}
          variant={variant}
          columns={columns}
          rowActions={rowActions}
          {...rest}
        />
      )
    );
    case 'User': return <UserRenderer entityId={entityId} variant={variant} {...rest} />;
    case 'Team': return <TeamRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />;
    case 'Contact': return <ContactRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />;
    case 'CreditGrant': return (
      <CreditGrantRenderer entityId={entityId} variant={variant} columns={columns} {...rest} />
    );
    case 'Organisation': return (
      variant === 'page' ? (
        <OrganisationPage
          organisationId={entityId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <OrganisationRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />
      )
    );
    case 'Portfolio': return (
      variant === 'page' ? (
        <PortfolioPage
          portfolioId={entityId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <PortfolioRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />
      )
    );
    case 'Project': return (
      variant === 'page' ? (
        <ProjectPage
          projectId={entityId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <ProjectRenderer entityId={entityId} variant={variant} {...rest} />
      )
    );
    case 'LegalEntity': return (
      variant === 'page' ? (
        <LegalEntityPage legalEntityId={entityId} hasEntityRowAbove={pageHasEntityRowAbove} className={pageClassName} />
      ) : (
        <LegalEntityRenderer entityId={entityId} variant={variant} columns={columns} rowActions={rowActions} {...rest} />
      )
    );
    case 'Lead': return (
      variant === 'page' ? (
        <LeadPage
          leadId={entityId}
          lockedUnitId={leadLockedUnitId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <LeadRenderer
          entityId={entityId}
          variant={variant}
          lockedUnitId={leadLockedUnitId}
          columns={columns}
          rowActions={rowActions}
          {...rest}
        />
      )
    );
    case 'Audit': return (
      variant === 'page' ? (
        <AuditPage
          auditId={entityId}
          hasEntityRowAbove={pageHasEntityRowAbove}
          className={pageClassName}
        />
      ) : (
        <AuditRenderer
          entityId={entityId}
          variant={variant}
          isExpanded={rest.isExpanded}
          columns={columns}
          rowActions={rowActions}
          {...rest}
        />
      )
    );
    case 'SlideCorrection': return (
      <SlideCorrectionRenderer entityId={entityId} variant={variant} columns={columns} {...rest} />
    );
    default: return null;
  }
});
EntityView.displayName = 'EntityView';

